/** Dữ liệu iam-svc (GĐ 3.2): cây đơn vị, phạm vi dữ liệu của người dùng. W1: người dùng, vai trò, SoD, thiết bị, danh bạ. */
import type { AppUserStatus, IamUserFilter, LoginEventKind, PermissionChangeFilter } from '@kovix/api-client';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/app/auth/session.store';
import { api } from '@/shared/lib/api';
import { must } from '@/shared/lib/must';
import { needsRoleCatalog, resolveRoleNames } from './roleNames';

export const iamKeys = {
  orgUnits: () => ['iam-org-unit'] as const,
  userScope: (userId: string) => ['iam-user-scope', userId] as const,
  userPermissions: (userId: string) => ['iam-user-permissions', userId] as const,
  users: (f: IamUserFilter) => ['iam-user', f] as const,
  user: (id: string) => ['iam-user', 'one', id] as const,
  userStats: () => ['iam-user-stats'] as const,
  userQuota: () => ['iam-user-quota'] as const,
  jobTitles: () => ['iam-job-titles'] as const,
  userRoles: (id: string) => ['iam-user-roles', id] as const,
  userDevices: (id: string) => ['iam-user-devices', id] as const,
  userSessions: (id: string) => ['iam-user-sessions', id] as const,
  userTransitions: (id: string) => ['iam-user-transitions', id] as const,
  directory: (p: { q?: string | undefined; ids?: string[] | undefined }) => ['iam-directory', p] as const,
  roles: (p: { includeSystem?: boolean | undefined; includeInactive?: boolean | undefined }) =>
    ['iam-role', p] as const,
  role: (id: string) => ['iam-role', 'one', id] as const,
  roleUsers: (id: string) => ['iam-role-users', id] as const,
  capabilities: () => ['iam-capability'] as const,
  sodRules: () => ['iam-sod-rule'] as const,
  me: () => ['iam-me'] as const,
  myDevices: () => ['iam-me-devices'] as const,
  mySessions: () => ['iam-me-sessions'] as const,
  // X3 — scope 'tenant' (/api/v1/iam) | 'platform' (/api/v1/platform/iam)
  securityPolicy: (scope: SecurityScope) => ['iam-security-policy', scope] as const,
  securityPolicyHistory: (scope: SecurityScope) => ['iam-security-policy', scope, 'history'] as const,
  loginEvents: (scope: SecurityScope, p: { kind?: LoginEventKind | undefined; days?: number | undefined }) =>
    ['iam-login-event', scope, p] as const,
  platformAdmins: (f: { status?: AppUserStatus | undefined; q?: string | undefined }) =>
    ['iam-platform-admin', f] as const,
  platformAdmin: (id: string) => ['iam-platform-admin', 'one', id] as const,
  platformAdminStats: () => ['iam-platform-admin-stats'] as const,
  platformAdminRoles: (id: string) => ['iam-platform-admin-roles', id] as const,
  platformRoles: () => ['iam-platform-role'] as const,
  platformSodRules: () => ['iam-platform-sod-rule'] as const,
  // T3 — hàng chờ "Thay đổi quyền chờ duyệt" (tenant /permission-changes | platform /platform/permission-changes)
  permissionChanges: (scope: SecurityScope, f: PermissionChangeFilter) => ['iam-permission-change', scope, f] as const,
  permissionChange: (scope: SecurityScope, id: string) => ['iam-permission-change', scope, 'one', id] as const,
  permissionChangeTransitions: (scope: SecurityScope, id: string) =>
    ['iam-permission-change', scope, 'transitions', id] as const,
  // T9 A27 — SSO doanh nghiệp (IdP OIDC theo tenant)
  tenantIdps: () => ['iam-sso-idp'] as const,
  tenantIdpTransitions: (id: string) => ['iam-sso-idp', 'transitions', id] as const,
};

/** T9 A27: IdP SSO doanh nghiệp của tenant (không có bí mật). Chỉ ở chế độ tenant (`admin.config.view`). */
export function useTenantIdps(enabled = true) {
  return useQuery({
    queryKey: iamKeys.tenantIdps(),
    queryFn: ({ signal }) => api.iam.tenantIdps(signal).then((r) => r.data),
    enabled,
    staleTime: 15_000,
  });
}
export function useTenantIdpTransitions(id: string | undefined) {
  return useQuery({
    queryKey: iamKeys.tenantIdpTransitions(id ?? ''),
    queryFn: ({ signal }) => api.iam.tenantIdpTransitions(must(id, 'id'), signal).then((r) => r.data),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** T3: danh sách yêu cầu thay đổi quyền — làm mới 30 s (người khác gửi/duyệt). */
export function usePermissionChanges(scope: SecurityScope, f: PermissionChangeFilter = {}, enabled = true) {
  return useQuery({
    queryKey: iamKeys.permissionChanges(scope, f),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .permissionChanges({ limit: 200, ...f }, signal)
        .then((r) => r.data),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
}
export function usePermissionChange(scope: SecurityScope, id: string | undefined) {
  return useQuery({
    queryKey: iamKeys.permissionChange(scope, id ?? ''),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .permissionChange(id as string, signal)
        .then((r) => r.data),
    enabled: !!id,
    staleTime: 5_000,
  });
}
export function usePermissionChangeTransitions(scope: SecurityScope, id: string | undefined) {
  return useQuery({
    queryKey: iamKeys.permissionChangeTransitions(scope, id ?? ''),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .permissionChangeTransitions(id as string, signal)
        .then((r) => r.data),
    enabled: !!id,
    staleTime: 10_000,
  });
}

// ---- X3 chính sách bảo mật (tenant /admin/security; platform /platform/access?tab=security) + quản trị viên nền tảng ----
export type SecurityScope = 'tenant' | 'platform';
/** Cùng bộ API cho hai phạm vi — trang dùng `securityApi(scope)` để không nhân đôi component. */
export const securityApi = (scope: SecurityScope) => (scope === 'platform' ? api.platformIam : api.iam);

export function useSecurityPolicy(scope: SecurityScope) {
  return useQuery({
    queryKey: iamKeys.securityPolicy(scope),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .securityPolicy(signal)
        .then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useSecurityPolicyHistory(scope: SecurityScope, enabled = true) {
  return useQuery({
    queryKey: iamKeys.securityPolicyHistory(scope),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .securityPolicyHistory(20, signal)
        .then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}
export function useLoginEvents(
  scope: SecurityScope,
  p: { kind?: LoginEventKind | undefined; days?: number | undefined; limit?: number | undefined } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: iamKeys.loginEvents(scope, { kind: p.kind, days: p.days }),
    queryFn: ({ signal }) =>
      securityApi(scope)
        .loginEvents(p, signal)
        .then((r) => r.data),
    enabled,
    staleTime: 10_000,
  });
}
/** `enabled = false` khi ở phạm vi tenant (token tenant không gọi được /api/v1/platform/** — T6). */
export function usePlatformAdmins(
  f: { status?: AppUserStatus | undefined; q?: string | undefined; limit?: number | undefined } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: iamKeys.platformAdmins({ status: f.status, q: f.q }),
    queryFn: ({ signal }) => api.platformIam.admins(f, signal).then((r) => r.data),
    staleTime: 15_000,
    enabled,
    retry: false,
  });
}
export function usePlatformAdmin(id: string, enabled = true) {
  return useQuery({
    queryKey: iamKeys.platformAdmin(id),
    queryFn: ({ signal }) =>
      api.platformIam.admin(id, signal).then((r) => ({ ...r.data, etag: r.etag ?? `"${r.data.version}"` })),
    enabled,
    staleTime: 10_000,
  });
}
export function usePlatformAdminStats() {
  return useQuery({
    queryKey: iamKeys.platformAdminStats(),
    queryFn: ({ signal }) => api.platformIam.adminStats(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function usePlatformAdminRoles(id: string, enabled = true) {
  return useQuery({
    queryKey: iamKeys.platformAdminRoles(id),
    queryFn: ({ signal }) => api.platformIam.adminRoles(id, signal).then((r) => r.data),
    enabled,
    staleTime: 10_000,
  });
}
export function usePlatformRoles(enabled = true) {
  return useQuery({
    queryKey: iamKeys.platformRoles(),
    queryFn: ({ signal }) => api.platformIam.platformRoles(signal).then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}
export function usePlatformSodRules() {
  return useQuery({
    queryKey: iamKeys.platformSodRules(),
    queryFn: ({ signal }) => api.platformIam.platformSodRules(signal).then((r) => r.data),
    staleTime: 60_000,
  });
}

// ---- W2 tự phục vụ ----
/** `enabled = false` ở chế độ platform (token platform không gọi iam tenant — T6). */
export function useMe(enabled = true) {
  return useQuery({
    queryKey: iamKeys.me(),
    queryFn: ({ signal }) => api.iam.me(signal).then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}

/**
 * T6 V-PF-25 — tên vai trò thay mã (`permissions.roles` là MÃ). KOVIX-2448 (BR-18.22): ưu tiên `known` = `permissions.roleNames` (iam trả tên vai trò của
 * chính người dùng, không cần quyền); chỉ tải `/roles` (cần `admin.role.view` — `enabled`) khi còn mã chưa có tên; còn lại → mã được làm gọn.
 * Không truyền `known` → tự lấy `permissions.roleNames` của phiên, để không nơi gọi nào sót nguồn tên (vd trang Tổng quan).
 */
export function useRoleNames(codes: string[], enabled: boolean, known?: Readonly<Record<string, string>>): string[] {
  const sessionNames = useSession((s) => s.permissions?.roleNames);
  const names = known ?? sessionNames;
  const { data } = useIamRoles({ includeSystem: true }, enabled && needsRoleCatalog(codes, names));
  return resolveRoleNames(codes, names, data);
}
export function useMyDevices() {
  return useQuery({
    queryKey: iamKeys.myDevices(),
    queryFn: ({ signal }) => api.iam.myDevices(signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useMySessions() {
  return useQuery({
    queryKey: iamKeys.mySessions(),
    queryFn: ({ signal }) => api.iam.mySessions(signal).then((r) => r.data),
    staleTime: 10_000,
  });
}

// ---- W1 ----
export function useIamUsers(f: IamUserFilter) {
  return useQuery({
    queryKey: iamKeys.users(f),
    queryFn: ({ signal }) => api.iam.users(f, signal).then((r) => r.data),
    staleTime: 15_000,
  });
}
/** Trả cả etag (If-Match cho thao tác ghi). */
export function useIamUser(id: string) {
  return useQuery({
    queryKey: iamKeys.user(id),
    queryFn: ({ signal }) =>
      api.iam.user(id, signal).then((r) => ({ ...r.data, etag: r.etag ?? `"${r.data.version}"` })),
    staleTime: 10_000,
  });
}
export function useIamUserStats() {
  return useQuery({
    queryKey: iamKeys.userStats(),
    queryFn: ({ signal }) => api.iam.userStats(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function useIamUserQuota() {
  return useQuery({
    queryKey: iamKeys.userQuota(),
    queryFn: ({ signal }) => api.iam.userQuota(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
/** M-426 — danh mục chức danh (bản sao từ Nhân sự, gồm cả chức danh đã ngừng để hiển thị người đang mang). */
export function useIamJobTitles() {
  return useQuery({
    queryKey: iamKeys.jobTitles(),
    queryFn: ({ signal }) => api.iam.jobTitles(true, signal).then((r) => r.data),
    staleTime: 60_000,
  });
}
export function useIamUserRoles(id: string) {
  return useQuery({
    queryKey: iamKeys.userRoles(id),
    queryFn: ({ signal }) => api.iam.userRoles(id, signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useIamUserDevices(id: string) {
  return useQuery({
    queryKey: iamKeys.userDevices(id),
    queryFn: ({ signal }) => api.iam.userDevices(id, signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useIamUserSessions(id: string) {
  return useQuery({
    queryKey: iamKeys.userSessions(id),
    queryFn: ({ signal }) => api.iam.userSessions(id, signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useIamUserTransitions(id: string) {
  return useQuery({
    queryKey: iamKeys.userTransitions(id),
    queryFn: ({ signal }) => api.iam.userTransitions(id, signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
/** Danh bạ cho UserName/UserPicker — ids nhận app_user.id hoặc keycloak_subject. */
export function useIamDirectory(
  p: { q?: string | undefined; ids?: string[] | undefined; limit?: number | undefined },
  enabled = true,
) {
  return useQuery({
    queryKey: iamKeys.directory({ q: p.q, ids: p.ids }),
    queryFn: ({ signal }) => api.iam.directory(p, signal).then((r) => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });
}
export function useIamRoles(
  p: { includeSystem?: boolean | undefined; includeInactive?: boolean | undefined } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: iamKeys.roles(p),
    queryFn: ({ signal }) => api.iam.roles(p, signal).then((r) => r.data),
    staleTime: 30_000,
    enabled,
  });
}
export function useIamRole(id: string) {
  return useQuery({
    queryKey: iamKeys.role(id),
    queryFn: ({ signal }) =>
      api.iam.role(id, signal).then((r) => ({ ...r.data, etag: r.etag ?? `"${r.data.version}"` })),
    staleTime: 10_000,
  });
}
export function useIamRoleUsers(id: string) {
  return useQuery({
    queryKey: iamKeys.roleUsers(id),
    queryFn: ({ signal }) => api.iam.roleUsers(id, signal).then((r) => r.data),
    staleTime: 10_000,
  });
}
export function useIamCapabilities(enabled = true) {
  return useQuery({
    queryKey: iamKeys.capabilities(),
    queryFn: ({ signal }) => api.iam.capabilities(signal).then((r) => r.data),
    enabled,
    staleTime: 10 * 60_000,
  });
}
export function useIamSodRules() {
  return useQuery({
    queryKey: iamKeys.sodRules(),
    queryFn: ({ signal }) => api.iam.sodRules(signal).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useIamOrgUnits() {
  return useQuery({
    queryKey: iamKeys.orgUnits(),
    queryFn: ({ signal }) => api.iam.orgUnits(true, signal).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useUserDataScope(userId: string, enabled = true) {
  return useQuery({
    queryKey: iamKeys.userScope(userId),
    queryFn: ({ signal }) => api.iam.userDataScope(userId, signal).then((r) => r.data ?? null),
    enabled,
    staleTime: 30_000,
  });
}

export function useUserPermissions(userId: string, enabled = true) {
  return useQuery({
    queryKey: iamKeys.userPermissions(userId),
    queryFn: ({ signal }) => api.iam.userPermissions(userId, signal).then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}
